<template>
  <div class="clearfix">
    <div v-if="showCheckedStatus || enable_edit || pdfDownload || showSocialSharing" class="float-end">
      <Tag v-if="showCheckedStatus" :severity="status_severity" :value="status_text"></Tag>
      <!-- <span v-if="showCheckedStatus" style="margin-right: 10px" v-html="status_span"></span> -->
      <!-- TMP: !hideAllAvatar to hide edit, cause edit crashes T20875#575172 in specific case -->
      <span v-if="showSocialSharing && context_type == 'thought'">
        <wdw-social-sharing
          :url="getCurrenThoughtUri(context_obj.id)"
          :title="context_obj.title"
          :description="truncate(context_obj.content)"
        />
      </span>
      <a
        v-if="enable_edit"
        class="click-link"
        @click="emitEdit"
        :aria-label="domainStore.translate('discuss.avatar.edit')"
        v-tooltip="domainStore.translate('discuss.avatar.edit')"
      >
        <font-awesome-icon icon="pencil-alt" aria-hidden="true" />
        <span class="visually-hidden">{{ domainStore.translate('discuss.avatar.edit') }}</span>
      </a>
      <a
        v-if="pdfDownload"
        class="click-link"
        @click="downloadPDF"
        v-tooltip="domainStore.translate('discuss.avatar.pdf-download')"
      >
        <font-awesome-icon icon="download" aria-hidden="true" />
        <span class="visually-hidden">{{ domainStore.translate('discuss.avatar.pdf-download') }}</span>
      </a>
    </div>
    <template v-if="!hideAllAvatar">
      <div v-if="showAvatar" class="discuss-avatar-image">
        <img
          class="avatar"
          :class="avatarClass"
          :src="avatarUrl"
          :width="avatar_size"
          :height="avatar_size"
          :title="userName"
        />
      </div>
      <div class="discuss-avatar">
        <p class="username" v-if="showName" :class="textClass" v-dompurify-html="userName"></p>
        <p class="created" :class="textClass">
          <span v-if="showDate" class="wdw-date" v-dompurify-html="formattedDate(UTCtoLocalTime(createdString))"></span>
          <span class="wdw-vote" v-if="showVoting">
            <template v-if="context_obj.is_pro">
              <a type="button" v-tooltip="domainStore.translate('discuss.avatar.voted-up')">
                <font-awesome-icon :icon="['far', 'thumbs-up']" aria-hidden="true" />
                <span class="visually-hidden">{{ domainStore.translate('discuss.avatar.voted-up') }}</span>
              </a>
            </template>
            <template v-if="context_obj.is_con">
              <a type="button" v-tooltip="domainStore.translate('discuss.avatar.voted-down')">
                <font-awesome-icon :icon="['far', 'thumbs-down']" aria-hidden="true" />
                <span class="visually-hidden">{{ domainStore.translate('discuss.avatar.voted-down') }}</span>
              </a>
            </template>
          </span>
          <span class="wdw-id" v-if="showId && showNumber">&nbsp;Nr. {{ context_obj.number }}</span>
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { onBeforeMount, toRefs, ref, watch, computed } from 'vue';
import { projectStore } from '@/store/project-store';
import thoughtRepository from '@/api/thoughtRepository';
import useThoughtHelpers from '@/composables/thought/useThoughtHelpers';
import useStringManip from '@/composables/general/useStringManip';
import useConverter from '@/composables/general/useConverter';
import WdwSocialSharing from '@/components/general/WdwSocialSharing.vue';
import { domainStore } from '@/store/domain-store';
import Tag from 'primevue/tag';

// TODO -> fully implement
// moment libarary
// surl for default avatars
// voting for comments
// pdfDownload for thoughts

export default {
  name: 'Avatar',
  props: {
    user: {
      type: Object,
      required: true,
    },
    anon: {
      type: Boolean,
      required: true,
    },
    avatar_size: {
      type: Number,
      default: 52,
    },
    context_type: {
      type: String,
      default: 'thought',
    },
    context_obj: {
      type: Object,
      required: true,
    },
    enable_edit: {
      type: Boolean,
      default: false,
    },
    show_status_label: {
      type: Boolean,
      default: false,
    },
    topic: {
      type: Object,
      required: false,
    },
    showSocialSharing: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['edit'],

  setup(props, context) {
    const { user, anon, avatar_size, context_type, context_obj, show_status_label } = toRefs(props);
    const { formattedDate, UTCtoLocalTime } = useConverter();
    const { getCurrenThoughtUri } = useThoughtHelpers();

    const { truncate } = useStringManip();

    // --------------------COMPUTED---------------------------
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const domain = computed(() => {
      return domainStore.getState().domain;
    });
    const avatarUrl = computed(() => {
      let url = user.value && user.value.avatar_uri ? user.value.avatar_uri : undefined;
      if (url == undefined || url == '') {
        url = domain.value.general_config.static_url + '/static/img/avatars/default_avatar_1-256.png';
      }
      return url;
    });
    const avatarClass = computed(() => {
      return 'avatar-' + props.avatar_size;
    });

    const showAvatar = computed(() => {
      if (props.context_type != 'thought' || !project.value.config?.thought_info_style) {
        return true;
      } else {
        return project.value.config.thought_info_style.includes('avatar') ? true : false;
      }
    });
    const showName = computed(() => {
      if (props.context_type != 'thought' || !project.value.config?.thought_info_style) {
        return true;
      } else {
        return project.value.config.thought_info_style.includes('name') ? true : false;
      }
    });
    const showDate = computed(() => {
      if (props.context_type != 'thought' || !project.value.config?.thought_info_style) {
        return true;
      } else {
        return project.value.config.thought_info_style.includes('date') ? true : false;
      }
    });
    const showNumber = computed(() => {
      if (props.context_type != 'thought' || !project.value.config?.thought_info_style) {
        return true;
      } else {
        return project.value.config.thought_info_style.includes('number') ? true : false;
      }
    });
    const hideAllAvatar = computed(() => {
      if (props.context_type != 'thought' || !project.value.config?.thought_info_style) {
        return false;
      } else {
        return project.value.config.thought_info_style.includes('none') ? true : false;
      }
    });

    // computed information, so it's reevaluated if the props change!
    const createdString = computed(() => {
      return context_obj.value.created;
    });
    const userName = computed(() => {
      return anon.value ? domainStore.translate('discuss.avatar.anonymous-user') : user.value.public_name;
    });
    const status_text = computed(() => {
      if (context_type.value == 'thought' && context_obj.value.status == 'show') {
        // only show the status for thoughts on hidden thoughts
        return '';
      }
      if (show_status_label.value) {
        if (context_obj.value.content_checked == 'ok') {
          return domainStore.translate('discuss.avatar.content_checked-status.ok');
        } else if (context_obj.value.content_checked == 'unchecked') {
          return domainStore.translate('discuss.avatar.content_checked-status.unchecked');
        } else if (context_obj.value.content_checked == 'misuse') {
          return domainStore.translate('discuss.avatar.content_checked-status.misuse');
        }
      }
      return '';
    });
    const status_severity = computed(() => {
      if (context_type.value == 'thought' && context_obj.value.status == 'show') {
        // only show the status for thoughts on hidden thoughts
        return 'success';
      }
      if (show_status_label.value) {
        if (context_obj.value.content_checked == 'ok') {
          return 'success';
        } else if (context_obj.value.content_checked == 'unchecked') {
          return 'warning';
        } else if (context_obj.value.content_checked == 'misuse') {
          return 'danger';
        }
      }
      return 'success';
    });
    const showVoting = computed(() => {
      if (context_type.value == 'comment') {
        return context_obj.value.is_pro || context_obj.value.is_con;
      }
      return false;
    });
    const showId = computed(() => {
      if (context_type.value == 'comment') {
        return false;
      }
      return true;
    });
    const textClass = computed(() => {
      if (context_type.value == 'comment') {
        return 'small';
      }
      return 'medium';
    });
    const pdfDownload = computed(() => {
      if (context_type.value == 'thought') {
        if (props.topic && props.topic.allow_pdf_download) {
          return true;
        }
      }
      return false;
    });
    const showCheckedStatus = computed(() => {
      return show_status_label.value && status_text.value != '';
    });

    function emitEdit(e) {
      e.stopPropagation();
      context.emit('edit', context_obj.value.id);
    }

    function downloadPDF(event) {
      event.stopPropagation();
      document.body.style.cursor = 'wait';
      thoughtRepository
        .getPDF(context_obj.value.id)
        .then(res => {
          if (res) {
            const fileURL = window.URL.createObjectURL(new Blob([res.data]));
            const fileLink = document.createElement('a');
            const pattern = /".*?"/g;
            const file = pattern.exec(res.headers['content-disposition']);
            const filename = file[0].replace(/["]+/g, '');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `${filename}.pdf`);
            document.body.appendChild(fileLink);
            fileLink.click();
            fileLink.remove();
          }
        })
        .finally(() => {
          document.body.style.cursor = 'default';
        });
    }

    return {
      domainStore,
      //--------COMPUTED-------
      avatarClass,
      showCheckedStatus,
      showAvatar,
      showName,
      showDate,
      showNumber,
      hideAllAvatar,
      avatarUrl,
      userName,
      createdString,
      showVoting, // TODO -> validate when displaying comments
      showId,
      textClass,
      pdfDownload,
      status_text,
      status_severity,
      //----------METHODS----------
      emitEdit,
      formattedDate,
      downloadPDF,
      UTCtoLocalTime,
      getCurrenThoughtUri,
      truncate,
    };
  },

  components: {
    Tag,
    WdwSocialSharing,
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/utils/mixins.scss';
p {
  margin: 0px;
  padding: 0px;
}
.discuss-avatar-image {
  float: left;
  margin-right: 10px;
  margin-bottom: 10px;
  .avatar-52 {
    width: 52px;
    height: 52px;
  }
  .avatar-36 {
    width: 36px;
    height: 36px;
  }
}

.discuss-avatar {
  @include clearfix();
  .username {
    margin-top: 4px;
    &.medium {
      font-size: 16px;
      margin-bottom: 5px;
      line-height: 1;
    }
    &.small {
      font-size: 14px;
      margin-bottom: 2px;
      line-height: 1;
    }
  }

  .created {
    color: var(--wdw-disuss-avatar-created-text-color, #6b6b6b);
    &.medium {
      font-size: 14px;
      margin-bottom: 5px;
      line-height: 1;
    }
    &.small {
      font-size: 12px;
      margin-bottom: 0;
      line-height: 1;
    }
  }
  .wdw-vote {
    padding-left: 5px;
  }
}
.click-link {
  margin-left: 5px;
}
</style>
